import { CommandModel } from "@/app/entities";
import { UserTypesCodes } from "@/composables/users";
import {
  DashboardHeader,
  UiTable,
  WhiteCard,
  UiButton,
  UiCardSearchInput,
  UiBadge,
  UiDialog,
  UiModal,
} from "@/components";
import { Head } from "@/components/seo";
import { useCommande } from "@/composables/commande";
import { Truck, Eye, Download, Link, Upload, FileText, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ChangeEvent, useEffect, useState } from "react";
import {
  CommandeFilterDataModel,
  CommandeFilterForm,
} from "@/components/molecules/commande";
import { useToast } from "@/composables/toast";
import { useCommandeStore } from "@/context";
import { formatAppDate, formatAppDateTime } from "@/utils";

export default function DashboardCommandes() {
  const navigate = useNavigate();
  const [modal, setModal] = useState<boolean>(false);
  const [commande, setCommande] = useState<CommandModel | null>(null);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const {
    loading,
    setFilterForm,
    fetchCommandes,
    dechargerCommande,
    getCommandeStatusBadgeCode,
    downloadBoucles,
  } = useCommande();

  const paginatedItems = useCommandeStore((state) => state.paginatedItems);
  const commandes = useCommandeStore((state) => state.paginatedItems.content);

  useEffect(() => {
    fetchCommandes({ userType: UserTypesCodes.Fournisseur });
  }, []);

  const handleDecharge = (row: CommandModel) => {
    setModal(true);
    setCommande(row);
  };

  const handleSubmit = async () => {
    if (!commande || !fileToUpload) {
      useToast("Le fichier semble manquant", { variant: "error" });
      return;
    }
    const res = await dechargerCommande({
      reference: commande.reference,
      file: fileToUpload,
    });

    if (res) {
      setModal(false);
      fetchCommandes({ userType: UserTypesCodes.Fournisseur });
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setFileToUpload(file);
    }
  };

  const removeFile = () => {
    setFileToUpload(null);
  };

  const columns = [
    {
      accessor: "auteur",
      title: "Auteur",
      render: (row: any) => {
        return (
          <span>
            {row.auteur?.nom ?? ""} {row.auteur?.prenom ?? ""}
          </span>
        );
      },
    },
    {
      accessor: "fournisseur",
      title: "Fournisseur",
      render: (row: any) => {
        return (
          <span>
            {row.fournisseur?.nom ?? ""} {row.fournisseur?.prenom ?? ""}
          </span>
        );
      },
    },
    {
      accessor: "villeLivraison",
      title: "Ville de livraison",
    },
    {
      accessor: "dfs",
      title: "Date de fabrication souhaitée",
      render: (row: any) => {
        return <span>{formatAppDate(row.dfs ?? "")}</span>;
      },
    },
    {
      accessor: "dateCreation",
      title: "Date de création",
      render: (row: any) => {
        return <span>{formatAppDateTime(row.dateCreation ?? "")}</span>;
      },
    },
    {
      accessor: "ddm",
      title: "Date de modification",
      render: (row: any) => {
        return <span>{formatAppDateTime(row.ddm ?? "")}</span>;
      },
    },
    {
      accessor: "statut",
      title: "Statut",
      render: (row: any) => {
        return (
          <UiBadge
            variant={getCommandeStatusBadgeCode(row)}
            className="block text-center"
          >
            {row.statut}
          </UiBadge>
        );
      },
    },
    {
      accessor: "campagne",
      title: "Campagne",
      render: (row: any) => {
        return (
          <Link
            href={`/dashboard/campagnes/${row.campagne?.reference ?? ""}`}
            className="text-blue-500"
          >
            Détail Campagne
          </Link>
        );
      },
    },
    {
      accessor: "actions",
      title: "Actions",
      render: (row: any) => {
        return (
          <div className="flex gap-2 flex-wrap">
            <UiButton
              size="small"
              onClick={() => navigate(`/dashboard/commandes/${row.reference}`)}
            >
              <Eye />
            </UiButton>
            {(row.statut === "FABRIQUE" ||
              row.statut === "NOTIFIER_FOURNISSEUR") && (
              <UiButton
                size="small"
                onClick={() => downloadBoucles(row.reference ?? "")}
              >
                <Download />
              </UiButton>
            )}

            {(row.statut === "FABRIQUE" ||
              row.statut === "EN_COUR_LIVRAISON") && (
              <UiButton
                color="success"
                size="small"
                onClick={() => handleDecharge(row)}
              >
                <Truck />
              </UiButton>
            )}
          </div>
        );
      },
    },
  ];

  const handleFilter = (data: CommandeFilterDataModel) => {
    setFilterForm(data);
  };

  return (
    <>
      <Head title="Commandes | Jourgui"></Head>
      <DashboardHeader subTitle="Bienvenue aux commandes" />

      <div className="py-4">
        <WhiteCard
          styles={{ borderRadius: "30px" }}
          headerSlot={
            <div className="flex items-center justify-end">
              <CommandeFilterForm
                defaultValue={{
                  status: "",
                  creationDate: "",
                  dfs: "",
                }}
                onChange={handleFilter}
              />
            </div>
          }
        >
          <div className="py-6">
            <UiTable
              columns={columns}
              rows={commandes}
              loading={loading}
              totalElements={paginatedItems.totalElements}
              totalPages={paginatedItems.totalPages}
              pageSize={paginatedItems.size}
              page={paginatedItems.number + 1}
            />
          </div>
        </WhiteCard>
      </div>

      <UiModal
        title="Décharger la commade"
        visible={modal}
        onClose={() => setModal(false)}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="ui-form-label text-sm font-medium text-gray-700">
              Fichier de déchargement
            </label>

            {!fileToUpload ? (
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  onChange={(e) => handleFileChange(e)}
                />
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200">
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <div className="text-sm text-gray-600">
                      <span className="font-medium text-blue-600 hover:text-blue-500">
                        Cliquez pour télécharger
                      </span>
                      <span className="text-gray-500"> ou glissez-déposez</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      PNG, JPG, GIF jusqu'à 10MB
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <FileText className="h-8 w-8 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {fileToUpload.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {(fileToUpload.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 gap-2 pt-4 border-t">
            <UiButton
              variant="outlined"
              onClick={() => setModal(false)}
              disabled={loading}
            >
              Annuler
            </UiButton>
            <UiButton
              loading={loading}
              onClick={handleSubmit}
              disabled={!fileToUpload}
              className="min-w-[120px]"
            >
              Décharger
            </UiButton>
          </div>
        </div>
      </UiModal>
    </>
  );
}
